import React, { createContext, useContext, ReactNode, useState, useCallback, useEffect } from 'react';
import { Project } from '../models/project.model';
import { projectService } from '../services/project/projectService';
import { userService } from '../services/user/userService';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

interface ProjectsContextType {
  isLoading: boolean;
  error: string | null;
  projects: Project[];
  currentProject: Project | null;
  fetchProjects: (organizationId: string, skipCache?: boolean) => Promise<void>;
  selectProject: (project: Project) => Promise<void>;
  createProject: (project: Partial<Project>) => Promise<Project>;
}

const ProjectsContext = createContext<ProjectsContextType | undefined>(undefined);

export const ProjectsProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);

  const fetchProjects = useCallback(async (organizationId: string, skipCache: boolean = false) => {
    if (!organizationId) {
      setError('No organization selected');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const projectList = await projectService.getProjectsByOrganizationId(organizationId);

      if (projectList.length === 0) {
        toast.warning(t('No projects found'));
        setProjects([]);
        setCurrentProject(null);
        setIsLoading(false);
        return;
      }

      setProjects(projectList);

      // Set active project or default to first one
      const activeProject = projectList.find(project => project.projectId === localStorage.getItem('selectedProjectId')) || projectList[0];
      setCurrentProject(activeProject);

      // Store meta connection status
      localStorage.setItem('meta_connected', activeProject.metaAccountId ? 'true' : 'false');
    } catch (err: any) {
      console.error('Failed to fetch projects:', err);
      setError(t('Failed to load projects'));
      toast.error(t('Failed to load projects'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const selectProject = useCallback(async (project: Project) => {
    try {
      setIsLoading(true);

      // Reset active status for all projects in local state
      const updatedProjects = projects.map(p => ({
        ...p,
        active: p.projectId === project.projectId // Only the selected project should be active
      }));

      setProjects(updatedProjects);

      // Set the selected project as active on the backend
      await userService.setActiveProject(project.projectId);

      // Update local state
      localStorage.setItem('selectedProjectId', project.projectId);

      setCurrentProject({ ...project, active: true });

      // Dispatch an event to notify other components that the project has changed
      window.dispatchEvent(new CustomEvent('project-changed', { detail: project }));
    } catch (err: any) {
      console.error('Failed to select project:', err);
      toast.error(t('Failed to select project'));
    } finally {
      setIsLoading(false);
    }
  }, [projects, t]);

  const createProject = useCallback(async (project: Partial<Project>) => {
    try {
      setIsLoading(true);

      const newProject = await projectService.createProject(project);

      // Add the new project to the list
      setProjects(prev => [...prev, newProject]);

      // If this is the first project, set it as current
      if (projects.length === 0) {
        setCurrentProject(newProject);
      }

      toast.success(t('Project created successfully'));

      // Dispatch an event to notify other components that projects have been updated
      window.dispatchEvent(new CustomEvent('force-refresh-projects'));

      return newProject;
    } catch (err: any) {
      console.error('Failed to create project:', err);
      toast.error(t('Failed to create project'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [projects.length, t]);

  // Listen for force refresh events
  useEffect(() => {
    const handleForceRefresh = (event: Event) => {
      console.log('Force refresh projects event received');
      const organizationId = localStorage.getItem('selectedOrganizationId');
      if (organizationId) {
        fetchProjects(organizationId, true);
      }
    };

    // Add event listener
    window.addEventListener('force-refresh-projects', handleForceRefresh);

    // Clean up
    return () => {
      window.removeEventListener('force-refresh-projects', handleForceRefresh);
    };
  }, [fetchProjects]);

  return (
    <ProjectsContext.Provider value={{
      isLoading,
      error,
      projects,
      currentProject,
      fetchProjects,
      selectProject,
      createProject
    }}>
      {children}
    </ProjectsContext.Provider>
  );
};

export const useProjectsContext = (): ProjectsContextType => {
  const context = useContext(ProjectsContext);
  if (context === undefined) {
    throw new Error('useProjectsContext must be used within a ProjectsProvider');
  }
  return context;
};
